"use client";
import { getAuth } from "firebase/auth";
import { addDoc, collection, getFirestore, onSnapshot, doc, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";

const STRIPE_PRICE_MAP = {
  'price_1SHY0cJcMfFv41GS0vap9SyS': 'weekly',
  'price_1T3O42JcMfFv41GSCpgnfQd7': 'weekly',
  'price_1RxCpCJcMfFv41GSDg7XdVcw': 'monthly',
  'price_1RxFCFJcMfFv41GSYCxUn7a6': 'monthly',
  'price_1RzGp0JcMfFv41GSBrskKB6S': 'yearly',
  'price_1RzJCqJcMfFv41GSlEninl5X': 'yearly',
};

export const getCheckoutUrl = async (priceId, planKey) => {
  const auth = getAuth();
  const userId = auth.currentUser?.uid;
  if (!userId) throw new Error("User is not authenticated");

  const normalizedPlan = planKey || STRIPE_PRICE_MAP[priceId] || 'monthly';
  const db = getFirestore();
  const functions = getFunctions(auth.app, "us-central1");

  // Step 1: Pre-purchase Cross-Platform Eligibility Check & Lock Reservation
  let lockRequestId = null;
  try {
    const checkEligibility = httpsCallable(functions, 'checkSubscriptionEligibility');
    const eligRes = await checkEligibility({
      provider: 'stripe',
      plan: normalizedPlan,
    });

    if (eligRes?.data && eligRes.data.allowed === false) {
      const err = new Error(eligRes.data.message || 'You already have an active subscription.');
      err.details = eligRes.data;
      throw err;
    }

    const reserveLock = httpsCallable(functions, 'reserveSubscriptionPurchase');
    const lockRes = await reserveLock({
      provider: 'stripe',
      plan: normalizedPlan,
    });

    if (lockRes?.data && lockRes.data.success === false) {
      const err = new Error(lockRes.data.error || 'A purchase session is already in progress for this account. Please wait a moment.');
      err.details = lockRes.data;
      throw err;
    }

    lockRequestId = lockRes?.data?.requestId || null;
  } catch (err) {
    if (err.details) throw err;
    console.warn("Stripe eligibility/reservation check warning:", err);
  }

  const checkoutSessionRef = collection(db, "customers", userId, "checkout_sessions");
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
  };

  const referralCode = getCookie('referralCode');
  const sessionData = {
    price: priceId,
    success_url: window.location.origin,
    cancel_url: window.location.origin,
    metadata: {
      userId: userId,
      price: priceId,
      plan: normalizedPlan,
      requestId: lockRequestId || '',
    }
  };

  if (referralCode) {
    sessionData.metadata.referral_code = referralCode;
  }

  const docRef = await addDoc(checkoutSessionRef, sessionData);

  return new Promise((resolve, reject) => {
    const unsubscribe = onSnapshot(docRef, (snap) => {
      const { error, url } = snap.data() || {};
      if (error) {
        unsubscribe();
        if (lockRequestId) {
          const releaseLock = httpsCallable(functions, 'releaseSubscriptionPurchase');
          releaseLock({ requestId: lockRequestId }).catch(() => {});
        }
        reject(new Error(error.message));
      }
      if (url) {
        unsubscribe();
        resolve(url);
      }
    }, (err) => {
      unsubscribe();
      if (lockRequestId) {
        const releaseLock = httpsCallable(functions, 'releaseSubscriptionPurchase');
        releaseLock({ requestId: lockRequestId }).catch(() => {});
      }
      reject(err);
    });
  });
};

export const getPortalUrl = async (app, { userDocId, email }) => {
  const db = getFirestore(app);
  const userDocRef = doc(db, "users", userDocId);
  const userDoc = await getDoc(userDocRef);
  let stripeId = userDoc.data()?.stripeId;

  if (!stripeId) {
    const functions = getFunctions(app, "us-central1");
    const createPortal = httpsCallable(functions, 'createPortalForUser');
    const result = await createPortal({ userDocId, email });
    return result.data.url;
  }

  const functions = getFunctions(app, "us-central1");
  const functionRef = httpsCallable(functions, "ext-firestore-stripe-payments-createPortalLink");
  const { data } = await functionRef({ customerId: stripeId, returnUrl: window.location.origin });
  const url = data && data.url;
  if (!url) throw new Error("No url returned");
  return url;
};
